import {
  CancelledError,
  Query,
  QueryClient,
  QueryKey,
} from "@tanstack/react-query";

import {
  WaitlistEntry,
  WaitlistStatus,
  WaitlistUpdateData,
} from "@/types/waitlist";

export interface WaitlistFilters {
  providerIds?: string[];
  desiredDate?: string;
  status?: WaitlistStatus[];
  patientId?: string;
  locationIds?: string[];
  matchProviderIds?: string[];
}
export const isWaitlistQuery = (query: { queryKey: QueryKey }) =>
  query.queryKey[0] === "waitlist";
export const waitlistQueryKeys = {
  all: ["waitlist"] as const,
  lists: () => ["waitlist", "list"] as const,
  detail: (id: string) => ["waitlist", "detail", id] as const,
  list: (
    providerIds?: string[],
    desiredDate?: string,
    status?: WaitlistStatus[],
    patientId?: string,
    locationIds?: string[],
    matchProviderIds?: string[],
  ) =>
    [
      "waitlist",
      "list",
      {
        providerIds: normalize(providerIds),
        desiredDate,
        status: normalize(status),
        patientId,
        locationIds: normalize(locationIds),
        matchProviderIds: normalize(matchProviderIds),
      },
    ] as const,
};
function normalize<T extends string>(values?: T[]) {
  return values?.length ? [...new Set(values)].sort() : undefined;
}
const ordered = (entries: WaitlistEntry[]) =>
  entries.sort(
    (a, b) =>
      a.createdDate.localeCompare(b.createdDate) || a.id.localeCompare(b.id),
  );

export function matchesWaitlistFilters(
  entry: WaitlistEntry,
  filters: WaitlistFilters,
): boolean {
  return (
    (filters.status?.length
      ? filters.status
      : [WaitlistStatus.WAITING]
    ).includes(entry.status) &&
    (!filters.patientId || entry.patientId === filters.patientId) &&
    (!filters.desiredDate ||
      (entry.startDate <= filters.desiredDate &&
        filters.desiredDate <= entry.endDate)) &&
    (!filters.locationIds?.length ||
      entry.locationId === null ||
      filters.locationIds.includes(entry.locationId)) &&
    (!filters.providerIds?.length ||
      filters.providerIds.some((id) => entry.matchingProviderIds?.includes(id)))
  );
}
export function projectWaitlistEntry(
  entry: WaitlistEntry,
  filters: WaitlistFilters,
): WaitlistEntry {
  const requested = new Set([
    ...(filters.providerIds ?? []),
    ...(filters.matchProviderIds ?? []),
  ]);
  return {
    ...entry,
    matchingProviderIds: requested.size
      ? (entry.matchingProviderIds ?? [])
          .filter((id) => requested.has(id))
          .sort()
      : null,
  };
}

export interface WaitlistRecoveryStatus {
  pending: boolean;
  error: Error | null;
}
export interface WaitlistCacheScope {
  identity: string | null;
  disposed: boolean;
  lastAlertDates: Map<string, number>;
  pendingReads: Set<Map<string, WaitlistUpdateData>>;
  recovery: Promise<void> | null;
  recoveryStatus: WaitlistRecoveryStatus;
}
const scopes = new WeakMap<QueryClient, WaitlistCacheScope>();
const recoveryListeners = new WeakMap<QueryClient, Set<() => void>>();
const EMPTY_RECOVERY_STATUS: WaitlistRecoveryStatus = {
  pending: false,
  error: null,
};
const notifyRecovery = (client: QueryClient) =>
  recoveryListeners.get(client)?.forEach((listener) => listener());
export function getWaitlistCacheScope(client: QueryClient): WaitlistCacheScope {
  let scope = scopes.get(client);
  if (!scope || scope.disposed) {
    scope = {
      identity: null,
      disposed: false,
      lastAlertDates: new Map(),
      pendingReads: new Set(),
      recovery: null,
      recoveryStatus: EMPTY_RECOVERY_STATUS,
    };
    scopes.set(client, scope);
  }
  return scope;
}
export function activateWaitlistCacheScope(
  client: QueryClient,
  identity: string,
) {
  const previous = getWaitlistCacheScope(client);
  if (previous.identity !== null && previous.identity !== identity)
    disposeWaitlistCacheScope(client, previous);
  const scope = getWaitlistCacheScope(client);
  scope.identity = identity;
  notifyRecovery(client);
  return scope;
}
export function isCurrentWaitlistScope(
  client: QueryClient,
  scope: WaitlistCacheScope,
) {
  return !scope.disposed && scopes.get(client) === scope;
}
export function disposeWaitlistCacheScope(
  client: QueryClient,
  scope: WaitlistCacheScope,
) {
  if (!isCurrentWaitlistScope(client, scope)) return;
  scope.disposed = true;
  scope.lastAlertDates.clear();
  scope.pendingReads.clear();
  notifyRecovery(client);
  void client.cancelQueries({ predicate: isWaitlistQuery });
  client.removeQueries({ predicate: isWaitlistQuery });
}
export function subscribeWaitlistRecovery(
  client: QueryClient,
  listener: () => void,
) {
  let listeners = recoveryListeners.get(client);
  if (!listeners) {
    listeners = new Set();
    recoveryListeners.set(client, listeners);
  }
  const observers = listeners;
  observers.add(listener);
  return () => {
    observers.delete(listener);
  };
}
export function getWaitlistRecoveryStatus(client: QueryClient) {
  const scope = scopes.get(client);
  return scope && !scope.disposed
    ? scope.recoveryStatus
    : EMPTY_RECOVERY_STATUS;
}
export function setWaitlistRecoveryStatus(
  client: QueryClient,
  scope: WaitlistCacheScope,
  status: WaitlistRecoveryStatus,
) {
  if (!isCurrentWaitlistScope(client, scope)) return;
  scope.recoveryStatus = status;
  notifyRecovery(client);
}

function patchRows(rows: WaitlistEntry[], change: WaitlistUpdateData) {
  return [
    ...rows.filter(
      (row) =>
        row.id !== change.waitlistId ||
        (!change.entry && row.locationId !== change.locationId),
    ),
    ...(change.entry ? [change.entry] : []),
  ];
}
function projectRows(rows: WaitlistEntry[], filters: WaitlistFilters) {
  return ordered(
    rows
      .filter((row) => matchesWaitlistFilters(row, filters))
      .map((row) => projectWaitlistEntry(row, filters)),
  );
}

/** Full payloads patch cached projections directly; only alerts carry ordering dates.
 * HTTP mutation results apply immediately without fabricating a server timestamp.
 */
export function applyWaitlistUpdate(
  client: QueryClient,
  scope: WaitlistCacheScope,
  change: WaitlistUpdateData,
  alertCreatedDate?: string,
): boolean {
  if (!isCurrentWaitlistScope(client, scope)) return false;
  const key = waitlistQueryKeys.detail(change.waitlistId);
  const current = client.getQueryData<WaitlistEntry | null>(key);
  // An old-location removal must not delete a row already moved elsewhere.
  if (!change.entry && current && current.locationId !== change.locationId)
    return false;
  if (alertCreatedDate !== undefined) {
    const date = Date.parse(alertCreatedDate);
    if (
      !Number.isFinite(date) ||
      date < (scope.lastAlertDates.get(change.waitlistId) ?? -Infinity)
    )
      return false;
    scope.lastAlertDates.set(change.waitlistId, date);
  }
  for (const changes of scope.pendingReads)
    changes.set(change.waitlistId, change);
  for (const query of client
    .getQueryCache()
    .findAll({ queryKey: waitlistQueryKeys.lists() })) {
    client.setQueryData<WaitlistEntry[]>(
      query.queryKey,
      (cached) =>
        cached &&
        projectRows(
          patchRows(cached, change),
          query.queryKey[2] as WaitlistFilters,
        ),
    );
  }
  client.setQueryData<WaitlistEntry | null>(
    key,
    (cached) =>
      change.entry ??
      (cached && cached.locationId !== change.locationId ? cached : null),
  );
  return true;
}

/** Booking confirms the status even if its best-effort pubsub alert is lost. */
export function applyWaitlistBooking(
  client: QueryClient,
  scope: WaitlistCacheScope,
  waitlistId: string,
) {
  if (!isCurrentWaitlistScope(client, scope)) return;
  const entry = client.getQueryData<WaitlistEntry | null>(
    waitlistQueryKeys.detail(waitlistId),
  );
  if (!entry) return;
  applyWaitlistUpdate(client, scope, {
    waitlistId,
    locationId: entry.locationId,
    entry: { ...entry, status: WaitlistStatus.BOOKED },
  });
}

/** Keep only changes received while this HTTP request is in flight. No persisted
 * row versions, recovery baseline or cross-request journal is maintained.
 */
export async function readWaitlistCache<T>(
  client: QueryClient,
  scope: WaitlistCacheScope,
  key: QueryKey,
  fetch: () => Promise<T>,
): Promise<T> {
  const changes = new Map<string, WaitlistUpdateData>();
  scope.pendingReads.add(changes);
  try {
    const data = await fetch();
    if (!isCurrentWaitlistScope(client, scope)) throw new CancelledError();
    let result: unknown = data;
    if (Array.isArray(data)) {
      let rows = data as WaitlistEntry[];
      for (const change of changes.values()) rows = patchRows(rows, change);
      result =
        key[1] === "list" ? projectRows(rows, key[2] as WaitlistFilters) : rows;
    } else {
      const change = changes.get(String(key[2]));
      if (change) {
        const existing = result as WaitlistEntry | null;
        result =
          change.entry ??
          (existing && existing.locationId !== change.locationId
            ? existing
            : null);
      }
    }
    return result as T;
  } finally {
    scope.pendingReads.delete(changes);
  }
}

/** Replace only the existing queries covered by the recovery snapshot. */
export function replaceWaitlistCache(
  client: QueryClient,
  scope: WaitlistCacheScope,
  rows: WaitlistEntry[],
  targets: ReadonlySet<Query>,
) {
  if (!isCurrentWaitlistScope(client, scope)) return;
  for (const query of targets) {
    if (client.getQueryCache().get(query.queryHash) !== query) continue;
    client.setQueryData(
      query.queryKey,
      query.queryKey[1] === "list"
        ? projectRows(rows, query.queryKey[2] as WaitlistFilters)
        : (rows.find((row) => row.id === query.queryKey[2]) ?? null),
    );
  }
}

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;
const nullableString = (value: unknown) =>
  value === null || typeof value === "string";
export function isWaitlistEntry(value: unknown): value is WaitlistEntry {
  if (!record(value)) return false;
  return (
    [
      "id",
      "patientId",
      "startDate",
      "endDate",
      "creatorId",
      "createdDate",
    ].every((key) => typeof value[key] === "string") &&
    nullableString(value.updatedDate) &&
    Object.values(WaitlistStatus).includes(value.status as WaitlistStatus) &&
    nullableString(value.locationId) &&
    nullableString(value.notes) &&
    Array.isArray(value.assignments) &&
    value.assignments.every(
      (item) =>
        record(item) &&
        typeof item.serviceId === "string" &&
        nullableString(item.providerId),
    ) &&
    (value.matchingProviderIds === null ||
      (Array.isArray(value.matchingProviderIds) &&
        value.matchingProviderIds.every((id) => typeof id === "string"))) &&
    (value.patient === null ||
      (record(value.patient) &&
        typeof value.patient.id === "string" &&
        nullableString(value.patient.firstName) &&
        nullableString(value.patient.lastName))) &&
    Array.isArray(value.providers) &&
    value.providers.every(
      (item) =>
        record(item) &&
        typeof item.id === "string" &&
        nullableString(item.firstName) &&
        nullableString(item.lastName),
    ) &&
    Array.isArray(value.services) &&
    value.services.every(
      (item) =>
        record(item) &&
        typeof item.id === "string" &&
        typeof item.name === "string",
    )
  );
}
export function isWaitlistUpdateData(
  value: unknown,
): value is WaitlistUpdateData {
  return (
    record(value) &&
    typeof value.waitlistId === "string" &&
    nullableString(value.locationId) &&
    (value.entry === null ||
      (isWaitlistEntry(value.entry) &&
        value.entry.id === value.waitlistId &&
        value.entry.locationId === value.locationId))
  );
}
